import { useCallback, useMemo, useState } from 'react'
import { NamingRule, SprintSuffix } from './generateSprints'

export const SUFFIX_TYPES: { value: SprintSuffix['type']; labelId: string }[] =
  [
    { value: 'date', labelId: 'bulkSprint.suffix.date' },
    { value: 'serial', labelId: 'bulkSprint.suffix.serial' },
  ]

export const MIN_SERIAL_START = 0
export const MAX_SERIAL_START = 9999
export const MIN_SERIAL_DIGITS = 1
export const MAX_SERIAL_DIGITS = 10

const DEFAULT_SERIAL_START = '1'
const DEFAULT_SERIAL_DIGITS = '2'

const clamp = (value: string, min: number, max: number, fallback: number) => {
  const parsed = parseInt(value, 10)
  if (isNaN(parsed)) return fallback
  return Math.max(min, Math.min(max, parsed))
}

export type NamingRuleState = {
  prefix: string
  setPrefix: (value: string) => void
  suffixType: SprintSuffix['type']
  setSuffixType: (value: SprintSuffix['type']) => void
  serialStartInput: string
  setSerialStartInput: (value: string) => void
  serialDigitsInput: string
  setSerialDigitsInput: (value: string) => void
  rule: NamingRule
  reset: () => void
}

export const useNamingRule = (defaultPrefix: string): NamingRuleState => {
  const [prefix, setPrefix] = useState<string>(defaultPrefix)
  const [suffixType, setSuffixType] = useState<SprintSuffix['type']>('date')
  const [serialStartInput, setSerialStartInput] =
    useState<string>(DEFAULT_SERIAL_START)
  const [serialDigitsInput, setSerialDigitsInput] = useState<string>(
    DEFAULT_SERIAL_DIGITS
  )

  const serialStart = clamp(
    serialStartInput,
    MIN_SERIAL_START,
    MAX_SERIAL_START,
    Number(DEFAULT_SERIAL_START)
  )
  const serialDigits = clamp(
    serialDigitsInput,
    MIN_SERIAL_DIGITS,
    MAX_SERIAL_DIGITS,
    MIN_SERIAL_DIGITS
  )

  // プレビュー再生成の useEffect が依存するため、値が変わらない限り参照を保つ
  const rule = useMemo<NamingRule>(
    () => ({
      prefix,
      suffix:
        suffixType === 'date'
          ? { type: 'date' }
          : { type: 'serial', start: serialStart, digits: serialDigits },
    }),
    [prefix, suffixType, serialStart, serialDigits]
  )

  const reset = useCallback(() => {
    setPrefix(defaultPrefix)
    setSuffixType('date')
    setSerialStartInput(DEFAULT_SERIAL_START)
    setSerialDigitsInput(DEFAULT_SERIAL_DIGITS)
  }, [defaultPrefix])

  return {
    prefix,
    setPrefix,
    suffixType,
    setSuffixType,
    serialStartInput,
    setSerialStartInput,
    serialDigitsInput,
    setSerialDigitsInput,
    rule,
    reset,
  }
}
